import { useQuery } from '@tanstack/react-query'
import { fetchPortalHome } from '../api/portal'
import { useSession } from '../useSession'
import PasskeysView from './PasskeysView'
import PushNotificationsView from './PushNotificationsView'
import { HouseholdPanel } from './PortalView'
import BackLink from './BackLink'
import DancerVisibilityPanel from './DancerVisibilityPanel'
import DancerLoginPanel from './DancerLoginPanel'
import CalendarLinksPanel from './CalendarLinksPanel'
import JackrabbitPortalLink from './JackrabbitPortalLink'
import OwnPinPanel from './OwnPinPanel'
import PaymentLinkPanel from './PaymentLinkPanel'
import RegularHoursPanel from './RegularHoursPanel'
import DayChangePanel from './DayChangePanel'
import TimeOffPanel from './TimeOffPanel'
import AccountGroup from './AccountGroup'
import { fetchMyHours } from '../api/regularHours'
import { fetchMyTimeOff } from '../api/timeOff'
import { today } from '../api/calendar'
import { hoursSummary } from '../accountSummary'

/**
 * Your own account, for everyone (D-320).
 *
 * **A screen rather than a panel in one portal**, because all three kinds of
 * person need it: a parent, a teacher, and Tonya, who has no portal at all and
 * would otherwise have had nowhere to set a passkey up.
 *
 * **A parent also finds her household here** (D-322). That was the portal's
 * Account tab, and the tabs are gone — every other one became a destination
 * with a URL, and this is the one that had none to go to, because Account is
 * not a destination: it is in the header menu for every role already, which is
 * why D-313 moved Jackrabbit billing there rather than onto the rail.
 *
 * Her family and the way she signs in is what "my account" means to a parent,
 * and holding half of it on each of two screens is how the second half gets
 * forgotten.
 *
 * Still deliberately narrow: not a settings hub, and the account menu is how
 * you get here.
 *
 * **Grouped, and closed until opened** (D-719). It had grown to twelve boxes in
 * one column for a teacher who is also a parent. The order inside each group is
 * the order the comments below argue for; what changed is that like sits with
 * like, and a closed group says what is set in it.
 */
export default function AccountView() {
  const { session } = useSession()

  // Only a parent has one, and only a parent pays for the call.
  const { data: home } = useQuery({
    queryKey: ['portal'],
    queryFn: fetchPortalHome,
    enabled: session?.isParent === true,
  })

  const isKiosk = session?.isKiosk ?? false
  const isStaff = session?.isStaff ?? false
  const teaches = isStaff && !isKiosk

  // The same keys the panels inside read, so the summary costs no extra request.
  const { data: hours } = useQuery({ queryKey: ['my-hours'], queryFn: fetchMyHours, enabled: teaches })
  const { data: timeOff } = useQuery({ queryKey: ['my-time-off'], queryFn: fetchMyTimeOff, enabled: teaches })

  return (
    // Its own measure rather than `page-shell`: a single column of short
    // controls reads badly at 88rem, the same reason sign-in keeps a narrow one.
    <div className="mx-auto w-full max-w-[34rem] px-5 py-8">
      {/* Reached from the avatar menu, which is on every screen (D-353). */}
      <BackLink to="/" label="Home" />

      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Your account</p>
        <h1 className="font-display text-2xl">{session?.displayName}</h1>
        <p className="mt-1 text-[13.5px] wrap-anywhere text-ink-3">{session?.email}</p>
      </header>

      {!isKiosk && (
        <AccountGroup
          id="sign-in"
          title="Signing in & notifications"
          summary={isStaff ? 'Notifications, passkeys and your classroom PIN' : 'Notifications and passkeys'}
        >
          {/*
            Notifications first, because it is the one everybody has a reason to
            touch (D-530). Passkeys and a PIN answer "how do I get in"; this
            answers "will I hear about it".
          */}
          <PushNotificationsView isKiosk={isKiosk} />

          <PasskeysView isKiosk={isKiosk} />

          {/*
            Her classroom PIN, beside the other way she signs in (D-396). Holding
            one here and the other behind Tonya is what made a forgotten PIN a
            text message.
          */}
          <OwnPinPanel isStaff={isStaff} isKiosk={isKiosk} />
        </AccountGroup>
      )}

      {teaches && (
        <AccountGroup
          id="hours"
          title="Privates & your hours"
          summary={hoursSummary(session?.paymentLinks, hours, timeOff, today())}
        >
          {/* Where a family pays her (D-662): hers alone to set. */}
          <PaymentLinkPanel isStaff={isStaff} isKiosk={isKiosk} paymentLinks={session?.paymentLinks ?? []} />

          {/*
            When she is at the studio (D-674), the first thing she owns about
            herself that changes what a family may ask her for.
          */}
          <RegularHoursPanel
            isStaff={isStaff}
            isKiosk={isKiosk}
            canHoldRooms={session?.capabilities?.includes('BookRooms') ?? false}
          />

          {/*
            Then one day that differs from that week (D-684), then the dates she
            is away (D-681). The order is the argument: the rule, one date that
            amends it, and dates nobody may book at all - read the other way
            round, it invites her to record a holiday as a hole in every week.
          */}
          <DayChangePanel isStaff={isStaff} isKiosk={isKiosk} />

          <TimeOffPanel isStaff={isStaff} isKiosk={isKiosk} />
        </AccountGroup>
      )}

      {session?.isParent === true && (
        <AccountGroup
          id="family"
          title="Your family"
          summary={
            home
              ? `${home.dancers.length === 1 ? '1 dancer' : `${home.dancers.length} dancers`} · who you see, dancer logins, household details`
              : 'Who you see, dancer logins, household details'
          }
        >
          {/*
            Who she sees, above the household details (D-381): a control rather
            than a record, and more likely what she came to change. Then who can
            sign in for themselves (D-539), the bigger step.
          */}
          <DancerVisibilityPanel />

          <DancerLoginPanel />

          {home && <HouseholdPanel home={home} />}
        </AccountGroup>
      )}

      {/*
        A group of its own, closed like the rest (`asked`, 2026-09-17). It was
        the one block on this page always open, and with three dancers it was
        most of the scroll on a phone. Below the family group, because "who you
        see" decides which rows appear inside it (D-533): hiding a dancer empties
        her calendar with nobody revoking anything.
      */}
      <CalendarLinksPanel isKiosk={isKiosk} grouped />

      {/*
        **Billing, at the bottom and never inside a group** (`asked`,
        2026-09-17: *"the jack rabbit parent portal link should prob always be
        visible at the bottom of the page not hidden inside family"*).
        It is the way out of this app to the system that owns billing (D-111,
        D-259), so a parent has to be able to see it without knowing which group
        to open - and a closed group is exactly how it was hidden.

        Only a parent, because only a family has a Jackrabbit portal to reach.
        The component draws nothing when the studio has no link set (D-396).
      */}
      {session?.isParent === true && !isKiosk && (
        <div className="mt-6">
          <JackrabbitPortalLink />
        </div>
      )}
    </div>
  )
}
